"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight } from "lucide-react";

export default function Breadcrumbs() {
  const pathname = usePathname();
  
  // Don't show breadcrumbs on home page
  if (pathname === "/") {
    return null;
  }

  // Split pathname into segments
  const segments = pathname.split("/").filter(Boolean);
  
  // Build breadcrumb items
  const breadcrumbItems = segments.map((segment, index) => {
    const href = "/" + segments.slice(0, index + 1).join("/");
    const isLast = index === segments.length - 1;
    const prevSegment = index > 0 ? segments[index - 1] : null;
    
    // Format label - decode URL encoding and capitalize
    let label = decodeURIComponent(segment)
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    
    // Special handling for common routes
    const routeLabels: Record<string, string> = {
      stores: "Stores",
      coupons: "Coupons",
      categories: "Categories",
      blogs: "Blogs",
      about: "About",
      contactus: "Contact Us",
    };
    
    // If it's a known route, use the label
    if (routeLabels[segment.toLowerCase()]) {
      label = routeLabels[segment.toLowerCase()];
    }
    // If it's a dynamic route (slug), keep the formatted label
    // For stores/[slug], blogs/[slug], etc., the slug will be shown as-is (formatted)
    else if (prevSegment && (prevSegment === "stores" || prevSegment === "blogs")) {
      // For store/blog slugs, show the formatted name
      // The label is already formatted above
    }
    
    return {
      href,
      label,
      isLast,
    };
  });

  return (
    <nav className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 py-4 px-4 md:px-10" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-1.5 md:space-x-2 text-sm text-gray-600 max-w-7xl mx-auto pl-4 md:pl-6">
        <li>
          <Link 
            href="/" 
            className="hover:text-purple-600 transition-colors duration-200 font-medium text-gray-700 hover:underline decoration-purple-600 underline-offset-2"
          >
            Home
          </Link>
        </li>
        {breadcrumbItems.map((item, index) => (
          <li key={item.href} className="flex items-center">
            <ChevronRight className="w-4 h-4 mx-1.5 md:mx-2 text-gray-400 flex-shrink-0" />
            {item.isLast ? (
              <span 
                className="text-purple-700 font-semibold truncate max-w-[200px] md:max-w-none" 
                aria-current="page"
                title={item.label}
              >
                {item.label}
              </span>
            ) : (
              <Link 
                href={item.href} 
                className="hover:text-purple-600 transition-colors duration-200 font-medium text-gray-700 hover:underline decoration-purple-600 underline-offset-2 truncate max-w-[200px] md:max-w-none"
                title={item.label}
              >
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

